import { fetchQuery, graphql } from 'react-relay';

import { APIConfig } from 'src/config';
import {
  DATA_CONSUMED_KEY, DATA_GENERATED_KEY, DEFAULT_FETCH_QUERY_TIMEOUT, DEFAULT_MAX_RETRIES,
  ELEC_ENERGY_CONSUMED, ELEC_ENERGY_GENERATED,
  METER_LIST_PAGE_SIZE, TRADE_RULE_LIST_PAGE_SIZE,
} from 'src/util/constants';
import { queryWithRetry } from 'src/util/graphql';
import BatchQueryError from 'src/util/graphqlErrors';
import logger from 'src/util/logger';
import {
  calculateTradeSetSummaryBatchSize,
  generateBatchTimeRanges,
} from 'src/util/tradeSetSummary/batchCalculator';

import { PROPERTY_SHOW_QUERY_TYPES } from './constants';
import { PropertyShowLoadingActions } from '../reducer/constants';

// GraphQL Queries
// Query 1: Meters data. Paginated to handle properties with many meters.
const metersQuery = graphql`
query dataBatchingMetersQuery(
  $uuid: ID!,
  $pageSize: Int,
  $after: String
) @throwOnFieldError {
  property(uuid:$uuid) {
    meters(first: $pageSize, after: $after) {
      edges {
        node {
          uuid
          identifier
          externalIdentifier
          active { start finish }
          title
          tradePointId
          property {
            title
          }
          primaryBillingPoint {
            identifier
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
}
`;

// Query 2: Property rule count. Used to gate and size trade set summary batches.
const propertyRuleCountQuery = graphql`
query dataBatchingPropertyRuleCountQuery(
  $uuid: ID!,
  $start: Timestamp!,
  $finish: Timestamp!
) @throwOnFieldError {
  property(uuid: $uuid) {
    rulesCount(start: $start, finish: $finish)
  }
}
`;

// Query 3: Rules data. Paginated to handle meters with many rules.
const rulesQuery = graphql`
query dataBatchingRulesQuery(
  $meterId: ID!,
  $start: Timestamp!,
  $finish: Timestamp!,
  $pageSize: Int,
  $after: String
) @throwOnFieldError {
  meter(uuid: $meterId) {
    uuid
    rules(start: $start, finish: $finish, first: $pageSize, after: $after) {
      edges {
        node {
          uuid
          tradeType
          buyer {
            userId
            user {
              uuid
              email
              givenName
              familyName
            }
            tradePoint {
              uuid
              type
              meter {
                uuid
                identifier
                title
                property {
                  uuid
                  title
                }
              }
            }
          }
          seller {
            userId
            user {
              uuid
              email
              givenName
              familyName
            }
            tradePoint {
              uuid
              type
              meter {
                uuid
                identifier
                title
                property {
                  uuid
                  title
                }
              }
            }
          }
          state
          clauses {
            edges {
              node {
                price
                ignorePublicHolidays
                monthsOfYear
                daysOfWeek
                timesOfDay {
                  start { hours minutes }
                  finish { hours minutes }
                }
              }
            }
          }
          proposedAt
          proposedBy {
            uuid
            email
            givenName
            familyName
          }
          proposedById
          acceptedAt
          acceptedBy {
            uuid
            email
            givenName
            familyName
          }
          acceptedById
          start
          finish
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
}
`;

// Query 4: Meter data (consumed/generated) - unified query with metric parameter
const meterDataQuery = graphql`
query dataBatchingMeterDataQuery(
  $meterId: ID!,
  $batchStart: Timestamp!,
  $batchFinish: Timestamp!,
  $aggregation: String!,
  $timeZone: String!,
  $metric: String!
) @throwOnFieldError {
  meter(uuid: $meterId) {
    uuid
    calculatedData(start: $batchStart, finish: $batchFinish, aggregation: $aggregation, timeZone: $timeZone, metric: $metric) {
      aggregation
      metric {
        identifier
      }
      timeZone
      timeRange {
        start
        finish
      }
      data {
        timestamp
        value
        flags {
          identifier
          description
          Valid
        }
      }
    }
  }
}
`;

// Query 5: Trade set summary for a meter.
const meterTradeSetSummaryQuery = graphql`
query dataBatchingMeterTradeSetSummaryQuery(
  $meterId: ID!,
  $batchStart: Timestamp!,
  $batchFinish: Timestamp!,
  $timeZone: String!,
  $tradeAggregation: String!
) @throwOnFieldError {
  meter(uuid: $meterId) {
    uuid
    tradeSetSummary(start: $batchStart, finish: $batchFinish, timeZone: $timeZone, aggregation: $tradeAggregation, groups: [SUMMARY_GROUP_TRADE_POINT, SUMMARY_GROUP_TRADE_RULE, SUMMARY_GROUP_DIRECTION, SUMMARY_GROUP_TRADE_TYPE]) {
      key {
        tradePointId
        ruleId
        direction
        type
      }
      data {
        range { start finish }
        buyerTradePointIds
        sellerTradePointIds
        directions
        types
        value
        volume
        averagePrice
        tradesCount
        intervalsCount
      }
    }
  }
}
`;

/**
 * Builds a map of distinct trade rules keyed by rule UUID from per-meter rules
 * data held locally or in a cached segment. A trade rule links a buyer and a
 * seller meter, so the same rule can appear under more than one meter's
 * `rules.edges`; keying by `node.uuid` collapses those duplicates so each rule
 * is represented once. Edges without a `node.uuid` are ignored.
 * @param {Array} rulesData - Array of per-meter objects shaped `{ rules: { edges } }`
 * @returns {object} Map of `uuid -> rule node` for each distinct rule ({} on malformed input)
 * @see getDistinctRulesCount
 */
export const buildDistinctRules = (rulesData) => {
  if (!rulesData || !Array.isArray(rulesData)) {
    return {};
  }

  return rulesData.reduce((acc, meterRules) => {
    if (!meterRules?.rules?.edges?.length) { return acc; }

    meterRules.rules.edges.forEach((el) => {
      const uuid = el?.node?.uuid;
      if (uuid) {
        acc[uuid] = el.node;
      }
    });

    return acc;
  }, {});
};

/**
 * Client-side count of distinct trade rules across per-meter rules data held
 * locally or in a cached segment. Because a trade rule can appear under more
 * than one meter's `rules.edges`, this deduplicates by rule UUID so the result
 * matches the property's distinct rule count returned by `getPropertyRulesCount`.
 *
 * Used on the cache-hit and date-range reload paths, where rule edges are
 * already in memory, to derive the count without an extra network round-trip.
 * The fresh network path instead calls `getPropertyRulesCount` for the
 * authoritative server-side count.
 * @param {Array} rulesData - Array of per-meter objects shaped `{ rules: { edges } }`
 * @returns {number} Number of distinct rules (0 on malformed input)
 * @see buildDistinctRules
 * @see getPropertyRulesCount
 */
export const getDistinctRulesCount = (rulesData) => Object.keys(
  buildDistinctRules(rulesData),
).length;

/**
 * Fetch total rule count for a property across a date range.
 *
 * The schema declares `rulesCount: Int!` (non-null). When the backend resolver
 * throws, GraphQL null-bubbles up to the nearest nullable parent (`property`),
 * so the response arrives as `{ property: null }` — `rulesCount` is simply
 * absent. Because `fetchQuery.subscribe` delivers the raw network payload before
 * Relay normalises it, `@throwOnFieldError` has not yet fired at this point and
 * the absence is invisible to the subscriber. We therefore validate the returned
 * value explicitly and throw so the caller's `BatchQueryError` path is taken
 * instead of silently short-circuiting Stage 5 with a false zero.
 * @param {object} args - Function arguments
 * @param {string} args.propertyId - Property ID
 * @param {object} args.environment - Relay environment
 * @param {number} args.startSeconds - Start timestamp in seconds
 * @param {number} args.finishSeconds - Finish timestamp in seconds
 * @returns {Promise<number>} Property rule count
 * @throws {BatchQueryError} Tagged with `PROPERTY_SHOW_QUERY_TYPES.RULE_COUNT`
 *   when the fetch fails or the returned value is not a number
 */
export const getPropertyRulesCount = async (args) => {
  const {
    propertyId,
    environment,
    startSeconds,
    finishSeconds,
  } = args;

  const variables = {
    uuid: propertyId,
    start: startSeconds,
    finish: finishSeconds,
  };

  try {
    const data = await queryWithRetry({
      queryFn: () => {
        let resolvePromise;
        let rejectPromise;

        const promise = new Promise((resolve, reject) => {
          resolvePromise = resolve;
          rejectPromise = reject;
        });

        const subscription = fetchQuery(environment, propertyRuleCountQuery, variables).subscribe({
          next: (result) => {
            resolvePromise(result);
          },
          error: (error) => {
            rejectPromise(error);
          },
        });

        promise.unsubscribe = () => subscription.unsubscribe();

        return promise;
      },
      timeoutMs: APIConfig().GRAPH_API_TIMEOUT || DEFAULT_FETCH_QUERY_TIMEOUT,
      operationName: `Fetching rule count for property ${propertyId}`,
      maxRetries: DEFAULT_MAX_RETRIES,
    });

    const rulesCount = data?.property?.rulesCount;
    if (typeof rulesCount !== 'number') {
      // `rulesCount: Int!` is schema-non-null. A missing or null value means the
      // backend resolver failed and the error null-bubbled to `property`. Treat
      // this as a fetch failure rather than defaulting to 0, which would silently
      // skip Stage 5 even when rules may exist.
      throw new Error(
        `getPropertyRulesCount: unexpected response — rulesCount is ${rulesCount} (expected a number)`,
      );
    }
    return rulesCount;
  } catch (error) {
    logger.error('Error fetching property rule count:', error);
    throw new BatchQueryError(PROPERTY_SHOW_QUERY_TYPES.RULE_COUNT, error);
  }
};

/**
 * Helper function to merge data from multiple property data batches
 * This function makes the deep cloning and merging logic testable
 * @param {object} initialData - Initial property data to use as the base
 * @param {Array} batches - Array of data batches to merge
 * @returns {object} Merged property data
 */
export const mergePropertyDataBatches = (initialData, batches) => {
  if (!initialData || !batches || !batches.length) {
    logger.error('No initialData or batches provided to merge');
    return initialData;
  }

  logger.log(`Merging ${batches.length} property data batches`);

  try {
    let mergedData = {};
    try {
      mergedData = JSON.parse(JSON.stringify(initialData));
    } catch (cloneError) {
      logger.error('Error cloning initialData:', cloneError);
    }

    // Create map of meters by UUID for efficient lookups
    const metersMap = new Map();

    // Initialize meters map from merged data
    if (mergedData?.property?.meters?.edges) {
      mergedData.property.meters.edges.forEach((edge) => {
        const meter = edge?.node;
        if (meter?.uuid) {
          metersMap.set(meter.uuid, meter);
        }
      });
    }

    // Now merge all batches
    batches.forEach((batch) => {
      if (!batch?.property?.meters?.edges) return;

      batch.property.meters.edges.forEach((meterEdge) => {
        const batchMeterNode = meterEdge?.node;
        if (!batchMeterNode?.uuid) return;

        // Get or create meter in merged data
        if (!metersMap.has(batchMeterNode.uuid)) {
          metersMap.set(batchMeterNode.uuid, { ...batchMeterNode });
        }

        const mergedMeterNode = metersMap.get(batchMeterNode.uuid);

        // Merge rules
        if (batchMeterNode.rules?.edges) {
          if (!mergedMeterNode.rules) {
            mergedMeterNode.rules = { edges: [] };
          }

          // Create a Set of existing rule IDs to avoid duplicates when merging batches
          const existingRuleIds = new Set(
            mergedMeterNode.rules.edges?.map((e) => e?.node?.uuid) ?? [],
          );

          // Merge rules from this batch, skipping any that already exist in merged data
          batchMeterNode.rules.edges.forEach((ruleEdge) => {
            const rule = ruleEdge?.node;
            if (rule?.uuid && !existingRuleIds.has(rule.uuid)) {
              mergedMeterNode.rules.edges.push(ruleEdge);
              existingRuleIds.add(rule.uuid);
            }
          });
        }

        // Merge data consumed
        if (batchMeterNode[DATA_CONSUMED_KEY]?.data) {
          mergedMeterNode[DATA_CONSUMED_KEY] = batchMeterNode[DATA_CONSUMED_KEY];
        }

        // Merge data generated
        if (batchMeterNode[DATA_GENERATED_KEY]?.data) {
          mergedMeterNode[DATA_GENERATED_KEY] = batchMeterNode[DATA_GENERATED_KEY];
        }

        // Merge trade set summary
        if (Array.isArray(batchMeterNode.tradeSetSummary)) {
          if (!mergedMeterNode.tradeSetSummary) {
            mergedMeterNode.tradeSetSummary = [];
          }

          // Create a map of existing trades by key for efficient lookups
          const existingTradeMap = new Map();
          mergedMeterNode.tradeSetSummary.forEach((trade) => {
            const key = trade.key || {};
            const tradeKey = `${key.tradePointId}_${key.ruleId}_${key.direction}_${key.type}`;
            existingTradeMap.set(tradeKey, trade);
          });

          batchMeterNode.tradeSetSummary.forEach((trade) => {
            const key = trade.key || {};
            const tradeKey = `${key.tradePointId}_${key.ruleId}_${key.direction}_${key.type}`;

            if (existingTradeMap.has(tradeKey)) {
              // Merge data arrays for existing trade key
              const existingTrade = existingTradeMap.get(tradeKey);
              if (Array.isArray(existingTrade.data) && Array.isArray(trade.data)) {
                // Combine the data arrays from both batches
                existingTrade.data = [...existingTrade.data, ...trade.data];
              }
            } else {
              // Add new trade key
              mergedMeterNode.tradeSetSummary.push(trade);
              existingTradeMap.set(tradeKey, trade);
            }
          });
        }
      });
    });

    // Rebuild the meters edges array from the map
    if (!mergedData?.property) {
      mergedData.property = {};
    }
    if (!mergedData.property.meters) {
      mergedData.property.meters = {};
    }

    mergedData.property.meters.edges = Array.from(metersMap.values()).map((meter) => ({
      node: meter,
    }));

    logger.log(`Successfully merged ${batches.length} property data batches`);
    return mergedData;
  } catch (error) {
    logger.error('Error merging property data batches:', error);
    return initialData;
  }
};

/**
 * Get meters for the property with pagination support
 * @param {object} args - Arguments for fetching meters
 * @param {string} args.propertyId - Property ID
 * @param {object} args.environment - Relay environment
 * @param {number} [args.pageSize=METER_LIST_PAGE_SIZE] - Number of meters to fetch per page
 * @returns {Promise<Array>} Promise that resolves to array of meter objects
 */
export const getMeters = async (args) => {
  const {
    propertyId,
    environment,
    pageSize = METER_LIST_PAGE_SIZE,
  } = args;

  const allMeters = [];
  let hasNextPage = true;
  let afterCursor = '';

  while (hasNextPage) {
    const variables = {
      uuid: propertyId,
      pageSize,
      ...(afterCursor && { after: afterCursor }),
    };

    try {
      // eslint-disable-next-line no-await-in-loop
      const data = await queryWithRetry({
        queryFn: () => {
          let resolvePromise;
          let rejectPromise;

          const promise = new Promise((resolve, reject) => {
            resolvePromise = resolve;
            rejectPromise = reject;
          });

          const subscription = fetchQuery(environment, metersQuery, variables).subscribe({
            next: (result) => {
              resolvePromise(result);
            },
            error: (error) => {
              rejectPromise(error);
            },
          });

          promise.unsubscribe = () => subscription.unsubscribe();

          return promise;
        },
        timeoutMs: APIConfig().GRAPH_API_TIMEOUT || DEFAULT_FETCH_QUERY_TIMEOUT,
        operationName: `Fetching meters for property ${propertyId}`,
        maxRetries: DEFAULT_MAX_RETRIES,
      });

      if (!data?.property?.meters?.edges) {
        logger.warn('No meters data returned');
        break;
      }

      const pageMeters = data.property.meters.edges.map((edge) => edge.node);
      allMeters.push(...pageMeters);
      logger.log(`Stage 1: Fetched ${pageMeters.length} meters (total: ${allMeters.length})`);

      // Check if there are more pages
      hasNextPage = data.property.meters.pageInfo?.hasNextPage || false;
      afterCursor = data.property.meters.pageInfo?.endCursor || '';
    } catch (error) {
      logger.error('Error fetching meters page:', error);
      // Re-throw so the outer try/catch in getPropertyDataInBatches propagates to onError.
      // Swallowing here would surface as an empty meter list and a silent UI failure.
      throw new BatchQueryError(PROPERTY_SHOW_QUERY_TYPES.METERS, error);
    }
  }

  logger.log(`getMeters retrieved ${allMeters.length} meters`);
  return allMeters;
};

/**
 * Get trade rules for a meter with pagination support
 * @param {object} args - Arguments for fetching rules
 * @param {string} args.meterId - Meter ID
 * @param {object} args.start - start time (Luxon DateTime)
 * @param {object} args.finish - finish time (Luxon DateTime)
 * @param {object} args.environment - Relay environment
 * @param {number} [args.pageSize=TRADE_RULE_LIST_PAGE_SIZE] - Number of rules to fetch per page
 * @returns {Promise<object>} Promise that resolves to meter with rules data
 */
export const getRules = async (args) => {
  const {
    meterId,
    start,
    finish,
    environment,
    pageSize = TRADE_RULE_LIST_PAGE_SIZE,
  } = args;

  logger.log(`Stage 2: Fetching rules for meter ${meterId}`);

  const allRules = [];
  let hasNextPage = true;
  let afterCursor = '';

  while (hasNextPage) {
    const variables = {
      meterId,
      start: Math.floor(start.toSeconds()),
      finish: Math.floor(finish.toSeconds()),
      pageSize,
      ...(afterCursor && { after: afterCursor }),
    };

    try {
      // eslint-disable-next-line no-await-in-loop
      const data = await queryWithRetry({
        queryFn: () => {
          let resolvePromise;
          let rejectPromise;

          const promise = new Promise((resolve, reject) => {
            resolvePromise = resolve;
            rejectPromise = reject;
          });

          const subscription = fetchQuery(environment, rulesQuery, variables).subscribe({
            next: (result) => {
              resolvePromise(result);
            },
            error: (error) => {
              rejectPromise(error);
            },
          });

          promise.unsubscribe = () => subscription.unsubscribe();

          return promise;
        },
        timeoutMs: APIConfig().GRAPH_API_TIMEOUT || DEFAULT_FETCH_QUERY_TIMEOUT,
        operationName: `Fetching rules for meter ${meterId}`,
        maxRetries: DEFAULT_MAX_RETRIES,
      });

      if (!data?.meter?.rules?.edges) {
        logger.warn(`No rules data returned for ${meterId}`);
        break;
      }

      const pageRules = data.meter.rules.edges;
      allRules.push(...pageRules);
      logger.log(`Stage 2: Fetched ${pageRules.length} rules (total: ${allRules.length}) for meter ${meterId}`);

      // Check if there are more pages
      hasNextPage = data.meter.rules.pageInfo?.hasNextPage || false;
      afterCursor = data.meter.rules.pageInfo?.endCursor || '';
    } catch (error) {
      logger.error(`Error fetching rules page for meter ${meterId}:`, error);
      // Re-throw so the outer try/catch in getPropertyDataInBatches propagates to onError.
      // Swallowing here would surface as an empty meter list and a silent UI failure.
      throw new BatchQueryError(PROPERTY_SHOW_QUERY_TYPES.RULES, error);
    }
  }

  logger.log(`Stage 2: Found ${allRules.length} total rules for meter ${meterId}`);
  return { uuid: meterId, rules: { edges: allRules } };
};

/**
 * Fetch meter data (consumed or generated) for a single meter.
 * @param {object} args - Arguments for fetching meter data
 * @param {string} args.meterId - Meter ID
 * @param {object} args.start - start time (Luxon DateTime)
 * @param {object} args.finish - batch end time (Luxon DateTime)
 * @param {object} args.environment - Relay environment
 * @param {string} args.metric -
 * Metric type (e.g., "elec_energy_consumed" or "elec_energy_generated")
 * @param {string} args.historianAggregation - Aggregation level
 * @param {string} args.timezone - Timezone
 * @returns {Promise<object>} Promise that resolves to meter with data
 */
export const getMeterData = async (args) => {
  const {
    meterId,
    start,
    finish,
    environment,
    metric,
    historianAggregation,
    timezone,
  } = args;

  const metricIsConsumed = metric === ELEC_ENERGY_CONSUMED;
  const stage = metricIsConsumed ? 3 : 4;
  const key = metricIsConsumed ? DATA_CONSUMED_KEY : DATA_GENERATED_KEY;

  const variables = {
    meterId,
    batchStart: Math.floor(start.toSeconds()),
    batchFinish: Math.floor(finish.toSeconds()),
    aggregation: historianAggregation,
    timeZone: timezone,
    metric,
  };

  logger.log(`Stage ${stage}: Fetching ${metric} data for meter ${meterId}`);

  try {
    const data = await queryWithRetry({
      queryFn: () => {
        let resolvePromise;
        let rejectPromise;

        const promise = new Promise((resolve, reject) => {
          resolvePromise = resolve;
          rejectPromise = reject;
        });

        const subscription = fetchQuery(environment, meterDataQuery, variables).subscribe({
          next: (result) => {
            resolvePromise(result);
          },
          error: (error) => {
            rejectPromise(error);
          },
        });

        promise.unsubscribe = () => subscription.unsubscribe();

        return promise;
      },
      timeoutMs: APIConfig().GRAPH_API_TIMEOUT || DEFAULT_FETCH_QUERY_TIMEOUT,
      operationName: `Fetching ${metric} data for meter ${meterId}`,
      maxRetries: DEFAULT_MAX_RETRIES,
    });

    if (!data?.meter?.calculatedData) {
      logger.warn(`No ${metric} data returned for ${meterId}`);
      return { uuid: meterId, [key]: { data: [] } };
    }

    const dataCount = data.meter.calculatedData?.data?.length || 0;
    logger.log(`Stage ${stage}: Found ${dataCount} ${metric} data points for meter ${meterId}`);
    return { uuid: meterId, [key]: data.meter.calculatedData };
  } catch (error) {
    logger.error(`Error fetching ${metric} data for meter ${meterId}:`, error);
    throw new BatchQueryError(PROPERTY_SHOW_QUERY_TYPES.METER_DATA, error);
  }
};

/**
 * Fetch trade set summary for a single meter within batch time range
 * @param {object} args - Arguments for fetching trade set summary
 * @param {string} args.meterId - Meter ID
 * @param {object} args.start - start time (Luxon DateTime)
 * @param {object} args.finish - finish time (Luxon DateTime)
 * @param {object} args.environment - Relay environment
 * @param {string} args.tradeAggregation - Aggregation level
 * @param {string} args.timezone - Timezone
 * @returns {Promise<object>} Promise that resolves to meter with trade set summary
 */
export const getTradeSetSummaryForMeter = async (args) => {
  const {
    meterId,
    start,
    finish,
    environment,
    tradeAggregation,
    timezone,
  } = args;

  const variables = {
    meterId,
    batchStart: Math.floor(start.toSeconds()),
    batchFinish: Math.floor(finish.toSeconds()),
    tradeAggregation,
    timeZone: timezone,
  };

  logger.log(`Stage 5: Fetching trade set summary for ${JSON.stringify(variables)}`);

  try {
    const data = await queryWithRetry({
      queryFn: () => {
        let resolvePromise;
        let rejectPromise;

        const promise = new Promise((resolve, reject) => {
          resolvePromise = resolve;
          rejectPromise = reject;
        });

        const subscription = fetchQuery(
          environment,
          meterTradeSetSummaryQuery,
          variables,
        ).subscribe({
          next: (result) => {
            resolvePromise(result);
          },
          error: (error) => {
            rejectPromise(error);
          },
        });

        promise.unsubscribe = () => subscription.unsubscribe();

        return promise;
      },
      timeoutMs: APIConfig().GRAPH_API_TIMEOUT || DEFAULT_FETCH_QUERY_TIMEOUT,
      operationName: `Fetching trade set summary for meter ${meterId}`,
      maxRetries: DEFAULT_MAX_RETRIES,
    });

    if (!data?.meter?.tradeSetSummary) {
      logger.warn(`No trade set summary returned for ${JSON.stringify(variables)}`);
      return { uuid: meterId, tradeSetSummary: [] };
    }

    // Shallow-copy each summary entry so that mergePropertyDataBatches can
    // safely mutate the `data` array (append-only merge across time batches)
    // without corrupting the Relay store. A full deep-clone is unnecessary:
    // only the top-level `data` array reference is ever reassigned during merge.
    const tradeSetSummary = (data.meter.tradeSetSummary ?? []).map((entry) => ({
      ...entry,
      data: entry.data ? [...entry.data] : [],
    }));
    const tradeSetSummaryCount = tradeSetSummary.length;
    logger.log(`Stage 5: Found ${tradeSetSummaryCount} trade set summary items for ${JSON.stringify(variables)}`);
    return { uuid: meterId, tradeSetSummary };
  } catch (error) {
    logger.error(`Error fetching trade set summary for ${JSON.stringify(variables)}:`, error);
    throw new BatchQueryError(PROPERTY_SHOW_QUERY_TYPES.METER_TRADE_SET_SUMMARY, error);
  }
};

/**
 * Checks whether any meter has non-empty consumed or generated data.
 * When every meter's data arrays are empty (e.g. historian returned errors /
 * null calculatedData for all meters), there is no point fetching trade
 * summaries because trades cannot exist without underlying meter data.
 * @param {Array} consumedData - Array of { uuid, [DATA_CONSUMED_KEY]: { data: [] } }
 * @param {Array} generatedData - Array of { uuid, [DATA_GENERATED_KEY]: { data: [] } }
 * @returns {boolean} true when at least one meter has data points
 */
export const hasMeterData = (consumedData, generatedData) => {
  const hasConsumed = Array.isArray(consumedData)
    && consumedData.some((m) => m?.[DATA_CONSUMED_KEY]?.data?.length > 0);
  const hasGenerated = Array.isArray(generatedData)
    && generatedData.some((m) => m?.[DATA_GENERATED_KEY]?.data?.length > 0);
  return hasConsumed || hasGenerated;
};

/**
 * Merges meter data with rules, consumed, generated, and trade set summary data
 * @param {Array} meters - Array of meter objects from stage 1
 * @param {Array} rulesData - Array of meter objects with rules from stage 2
 * @param {Array} consumedData - Array of meter objects with consumed data from stage 3
 * @param {Array} generatedData - Array of meter objects with generated data from stage 4
 * @param {Array} tradeSummaryData - Array of meter objects with trade set summary from stage 5
 * @returns {object} Merged data in the expected format
 */
export const mergeBatchedData = (
  meters,
  rulesData,
  consumedData,
  generatedData,
  tradeSummaryData,
) => {
  // Guard clauses for parameters
  const validMeters = Array.isArray(meters) ? meters : [];
  const validRulesData = Array.isArray(rulesData) ? rulesData : [];
  const validConsumedData = Array.isArray(consumedData) ? consumedData : [];
  const validGeneratedData = Array.isArray(generatedData) ? generatedData : [];
  const validTradeSummaryData = Array.isArray(tradeSummaryData) ? tradeSummaryData : [];

  logger.log(`Merging five-stage data: ${validMeters.length} meters, ${validRulesData.length} rules, ${validConsumedData.length} consumed, ${validGeneratedData.length} generated, ${validTradeSummaryData.length} summary`);

  // Create maps for efficient lookups
  const rulesByMeterId = new Map();
  const consumedByMeterId = new Map();
  const generatedByMeterId = new Map();
  const summaryByMeterId = new Map();

  validRulesData.forEach((meterRules) => {
    if (meterRules.uuid) {
      rulesByMeterId.set(meterRules.uuid, meterRules.rules);
    }
  });

  validConsumedData.forEach((meterConsumed) => {
    if (meterConsumed.uuid) {
      consumedByMeterId.set(meterConsumed.uuid, meterConsumed[DATA_CONSUMED_KEY]);
    }
  });

  validGeneratedData.forEach((meterGenerated) => {
    if (meterGenerated.uuid) {
      generatedByMeterId.set(meterGenerated.uuid, meterGenerated[DATA_GENERATED_KEY]);
    }
  });

  validTradeSummaryData.forEach((meterSummary) => {
    if (meterSummary.uuid) {
      summaryByMeterId.set(meterSummary.uuid, meterSummary.tradeSetSummary);
    }
  });

  // Merge data for each meter
  const mergedMeters = validMeters.map((meter) => {
    const meterId = meter.uuid;
    return {
      ...meter,
      rules: rulesByMeterId.get(meterId) || { edges: [] },
      [DATA_CONSUMED_KEY]: consumedByMeterId.get(meterId) ?? { data: [] },
      [DATA_GENERATED_KEY]: generatedByMeterId.get(meterId) ?? { data: [] },
      tradeSetSummary: summaryByMeterId.get(meterId) ?? [],
    };
  });

  return {
    property: {
      meters: {
        edges: mergedMeters.map((meter) => ({ node: meter })),
      },
    },
  };
};

/**
 * Builds the single batch object passed to mergePropertyDataBatches for the energy-merge
 * step. Both getTimeSeriesData and getPropertyDataInBatches need this structure; the only
 * difference is whether rules are included.
 * @param {object} args
 * @param {Array} args.meters - Fetched meters array
 * @param {Array} args.consumedData - Array of { uuid, [DATA_CONSUMED_KEY]: { data: [] } }
 * @param {Array} args.generatedData - Array of { uuid, [DATA_GENERATED_KEY]: { data: [] } }
 * @param {Array} [args.rulesData] - Fetched rules data (required when includeRules is true)
 * @param {boolean} [args.includeRules=false] - When true, merges rules into each meter node
 * @returns {object} Batch object shaped for mergePropertyDataBatches
 */
const buildMeterMergeBatch = ({
  meters,
  consumedData,
  generatedData,
  rulesData,
  includeRules = false,
}) => {
  const consumedMap = new Map(consumedData.map((c) => [c.uuid, c]));
  const generatedMap = new Map(generatedData.map((g) => [g.uuid, g]));
  const rulesMap = includeRules ? new Map((rulesData ?? []).map((r) => [r.uuid, r])) : null;

  return {
    property: {
      meters: {
        edges: meters.map((meter) => {
          const meterConsumed = consumedMap.get(meter.uuid);
          const meterGenerated = generatedMap.get(meter.uuid);
          const node = {
            ...meter,
            [DATA_CONSUMED_KEY]: meterConsumed?.[DATA_CONSUMED_KEY] ?? { data: [] },
            [DATA_GENERATED_KEY]: meterGenerated?.[DATA_GENERATED_KEY] ?? { data: [] },
          };
          if (includeRules) {
            const meterRulesData = rulesMap.get(meter.uuid);
            node.rules = meterRulesData?.rules ?? { edges: [] };
          }
          return { node };
        }),
      },
    },
  };
};

/**
 * Fetch trade set summary for a property-level time batch
 * @param {object} args - Function arguments
 * @param {Array} args.meters - Array of meter objects
 * @param {object} args.batchStart - Start of the time batch (Luxon DateTime)
 * @param {object} args.batchFinish - End of the time batch (Luxon DateTime)
 * @param {object} args.environment - Relay environment
 * @param {string} args.tradeAggregation - Trade aggregation level
 * @param {string} args.timezone - Timezone
 * @param {object} [args.cancelledRef] - Ref to check if request has been cancelled
 * @returns {Promise<Array>} Promise that resolves to array of meter trade set summaries
 */
export const getPropertyTradeSetSummaryBatch = async (args) => {
  const {
    meters,
    batchStart,
    batchFinish,
    environment,
    tradeAggregation,
    timezone,
    cancelledRef,
  } = args;

  if (cancelledRef?.current) {
    return [];
  }

  return Promise.all(meters.map((meter) => getTradeSetSummaryForMeter({
    meterId: meter.uuid,
    start: batchStart,
    finish: batchFinish,
    environment,
    tradeAggregation,
    timezone,
  })));
};

/**
 * Builds the Stage 5 trade-summary Promise shared by getTimeSeriesData and
 * getPropertyDataInBatches. Encapsulates: batch calculation, initial progress
 * dispatch, concurrent per-batch fetches with per-batch progress updates, and
 * reshaping results into the { property: { meters: { edges } } } structure
 * expected by mergePropertyDataBatches.
 *
 * When rulesCount is 0 the fetch is skipped: an immediately-resolved empty
 * Promise is returned. The caller is responsible for dispatching any terminal
 * loading-state action (e.g. via handleSkippedTradeSummary).
 * @param {object} args
 * @param {number} args.rulesCount - Total number of trade rules (drives batch sizing and skip)
 * @param {Array} args.timeBatches - Pre-computed time batches from generateBatchTimeRanges
 * @param {Array} args.meters - Fetched meters array
 * @param {object} args.environment - Relay environment for GraphQL queries
 * @param {string} args.tradeAggregation - Aggregation level for trade data
 * @param {string} args.timezone - Timezone to use for data
 * @param {object} [args.cancelledRef] - Ref to check if request has been cancelled
 * @param {Function} [args.stateDispatchFunc] - Function to dispatch loading state actions
 * @returns {Promise<Array>} Resolves to an array of per-batch
 *   { property: { meters: { edges } } } objects, or [] when skipped
 */
const buildTradeSummaryPromise = ({
  rulesCount,
  timeBatches,
  meters,
  environment,
  tradeAggregation,
  timezone,
  cancelledRef,
  stateDispatchFunc,
}) => {
  // IMPORTANT: do NOT spread `...meter` into the batch nodes below.
  // `meters` holds live Relay store references. After the parallel fetch
  // (consumedPromise, generatedPromise, tradeSummaryPromise) resolves,
  // Relay may have updated those objects in place with energy + rules data.
  // Spreading `...meter` here would carry those fields into the trade-summary
  // batch, causing mergePropertyDataBatches to overwrite the already-correct
  // energy data with the aliased copy — inflating point counts.
  // The trade-summary batch only needs `uuid` + `tradeSetSummary`; all other
  // meter fields are already present in the initialData passed to the merge.
  const batchCount = timeBatches.length;

  if (rulesCount === 0) {
    logger.log('rulesCount is 0, skipping Stage 5 fetch');
    return Promise.resolve([]);
  }

  if (stateDispatchFunc) {
    stateDispatchFunc({
      type: PropertyShowLoadingActions.SET_TRADE_SUMMARY_PROGRESS,
      batchesLoaded: 0,
      batchesTotal: batchCount,
    });
  }

  let batchesCompleted = 0;
  return Promise.all(
    timeBatches.map((batch) => getPropertyTradeSetSummaryBatch({
      meters,
      batchStart: batch.start,
      batchFinish: batch.finish,
      environment,
      tradeAggregation,
      timezone,
      cancelledRef,
    }).then((result) => {
      batchesCompleted += 1;
      if (stateDispatchFunc && !cancelledRef?.current) {
        stateDispatchFunc({
          type: PropertyShowLoadingActions.SET_TRADE_SUMMARY_PROGRESS,
          batchesLoaded: batchesCompleted,
          batchesTotal: batchCount,
        });
      }
      return result;
    })),
  ).then((batches) => (
    // Transform array of meter-summary arrays into array of
    // { property: { meters: { edges } } } batches expected by mergePropertyDataBatches.
    batches.map((meterSummaries) => {
      const summaryMap = new Map(meterSummaries.map((s) => [s.uuid, s]));
      return {
        property: {
          meters: {
            edges: meters.map((meter) => ({
              node: {
                uuid: meter.uuid,
                tradeSetSummary: summaryMap.get(meter.uuid)?.tradeSetSummary ?? [],
              },
            })),
          },
        },
      };
    })
  ));
};

/**
 * Returns a shallow copy of data with tradeSetSummary reset to [] for every meter node.
 * Used in the trade-summary skip path to prevent stale summary data from initialData
 * surviving through mergePropertyDataBatches' append-only merge behaviour.
 * @param {object} data - Property data object
 * @returns {object} Data with tradeSetSummary set to [] for all meter nodes
 */
const buildDataWithClearedTradeSetSummaries = (data) => {
  if (!data?.property?.meters?.edges?.length) return data;
  return {
    ...data,
    property: {
      ...data.property,
      meters: {
        ...data.property.meters,
        edges: data.property.meters.edges.map((edge) => {
          if (!edge) return edge;
          return {
            ...edge,
            node: edge.node ? { ...edge.node, tradeSetSummary: [] } : edge.node,
          };
        }),
      },
    },
  };
};

/**
 * Handles the trade-summary skip path: logs the reason, dispatches a zero-count
 * SET_TRADE_SUMMARY_PROGRESS action, clears any stale tradeSetSummary from
 * initialData, merges the energy data, and invokes onDataReceivedFunc.
 *
 * The skip path is triggered when the historian returned null calculatedData for
 * all meters (hasMeterData is false) or when there are no trade rules.
 * Note: hard historian fetch errors throw BatchQueryError and propagate to
 * onError rather than reaching this path.
 *
 * When both conditions are true the logged reason reflects whichever branch of
 * the condition is checked first (no trade rules takes precedence).
 * @param {object} args
 * @param {string} args.reason - Human-readable skip reason for logging
 * @param {Array} args.meters - Fetched meters array
 * @param {Array} args.rulesData - Fetched rules data
 * @param {Array} args.consumedData - Fetched consumed data
 * @param {Array} args.generatedData - Fetched generated data
 * @param {object} args.initialData - Initial data to merge into
 * @param {Function} args.stateDispatchFunc - Dispatch function for loading state actions
 * @param {Function} args.onDataReceivedFunc - Callback to invoke with merged data
 */
const handleSkippedTradeSummary = ({
  reason,
  meters,
  rulesData,
  consumedData,
  generatedData,
  initialData,
  stateDispatchFunc,
  onDataReceivedFunc,
}) => {
  logger.log(`${reason}, skipping trade summary fetch`);
  if (stateDispatchFunc) {
    stateDispatchFunc({
      type: PropertyShowLoadingActions.SET_TRADE_SUMMARY_PROGRESS,
      batchesLoaded: 0,
      batchesTotal: 0,
    });
  }
  const batchData = mergeBatchedData(meters, rulesData, consumedData, generatedData, []);
  const clearedData = buildDataWithClearedTradeSetSummaries(initialData);
  const mergedData = mergePropertyDataBatches(clearedData, [batchData]);
  if (onDataReceivedFunc) {
    onDataReceivedFunc(mergedData, meters);
  }
};

/**
 * Fetch time series data (consumed, generated, trade summary) for a given time range
 * Handles batching of trade summary data based on historian aggregation and rules count
 * Used when dateSpan changes but meters/rules/property data remain the same
 * Batch size is calculated internally based on historian aggregation and rules count
 *
 * Loading state is managed via stateDispatchFunc which dispatches actions to the reducer.
 * @param {object} args - Arguments for the fetch operation
 * @param {Array} args.meters - Pre-fetched meters array
 * @param {Array} args.rulesData - Pre-fetched rules data
 * @param {object} args.dateSpan - Date span with start and finish properties (Luxon DateTimes)
 * @param {string} args.timezone - Timezone to use for data
 * @param {object} args.environment - Relay environment for GraphQL queries
 * @param {string} args.historianAggregation -
 * Aggregation level for historian data (e.g., 'PT30M', 'P1D')
 * @param {string} args.tradeAggregation - Aggregation level for trade data
 * @param {object} args.initialData - Initial data to merge batches with
 * @param {Function} args.onDataReceived - Callback when all data is received and merged
 * @param {Function} args.onError - Callback when an error occurs
 * @param {Function} args.stateDispatchFunc - Function to dispatch loading state actions
 * @param {object} args.isFetchingRef - Ref to track if fetching is in progress
 * @param {object} [args.cancelledRef] - Ref to check if request has been cancelled
 * @param {boolean} [args.subsequentLoad=false] - Whether this is a subsequent load (date range
 * change) vs initial load. When true, dispatches cached meters/rules counts to restore metadata
 * after START_REQUEST resets it.
 */
export const getTimeSeriesData = async (args) => {
  const {
    meters,
    rulesData,
    dateSpan,
    timezone,
    environment,
    historianAggregation,
    tradeAggregation,
    initialData,
    onDataReceived,
    onError,
    stateDispatchFunc,
    isFetchingRef,
    cancelledRef,
    subsequentLoad = false,
  } = args;

  // Safety check for required parameters
  if (!meters
    || !rulesData
    || !dateSpan
    || !timezone
    || !environment
    || !historianAggregation
    || !tradeAggregation) {
    if (onError) {
      onError(new Error('Missing required parameters for getTimeDependendData'));
    }
    return;
  }

  // Prevent parallel fetches
  if (isFetchingRef && isFetchingRef.current) {
    logger.log('Skipping time-dependent data fetch - another fetch is already in progress');
    return;
  }

  // Set fetching flag if available
  if (isFetchingRef) {
    isFetchingRef.current = true;
  }

  try {
    const { start, finish } = dateSpan || {};

    // Calculate distinct rules count and batch size
    const rulesCount = getDistinctRulesCount(rulesData);

    // For subsequent loads (date range changes), we need to dispatch the cached
    // meters and rules counts since START_REQUEST resets metadata to defaults.
    // We only do this for subsequent loads to avoid regressing the state machine
    // during initial load when these states have already been properly transitioned.
    if (subsequentLoad && stateDispatchFunc) {
      // Dispatch meters loaded with cached count
      stateDispatchFunc({
        type: PropertyShowLoadingActions.SET_METERS_LOADED,
        metersCount: meters.length,
      });
      // Dispatch rules loaded with cached count
      stateDispatchFunc({
        type: PropertyShowLoadingActions.SET_RULES_LOADED,
        rulesCount,
      });
    }

    // Check for cancellation before starting data fetch
    if (cancelledRef?.current) {
      logger.log('Time series data fetch cancelled before starting');
      return;
    }

    // Stages 3, 4 & 5: Fetch consumed, generated and trade summary data in parallel
    const consumedPromise = Promise.all(meters.map((meter) => getMeterData({
      meterId: meter.uuid,
      start,
      finish,
      environment,
      metric: ELEC_ENERGY_CONSUMED,
      historianAggregation,
      timezone,
    }))).then((data) => {
      if (stateDispatchFunc && !cancelledRef?.current) {
        stateDispatchFunc({ type: PropertyShowLoadingActions.SET_CONSUMED_DATA_LOADED });
      }
      return data;
    });

    const generatedPromise = Promise.all(meters.map((meter) => getMeterData({
      meterId: meter.uuid,
      start,
      finish,
      environment,
      metric: ELEC_ENERGY_GENERATED,
      historianAggregation,
      timezone,
    }))).then((data) => {
      if (stateDispatchFunc && !cancelledRef?.current) {
        stateDispatchFunc({ type: PropertyShowLoadingActions.SET_GENERATED_DATA_LOADED });
      }
      return data;
    });

    // Stage 5: Trade Summary
    const { batchSizeHours } = calculateTradeSetSummaryBatchSize({
      start,
      finish,
      aggregation: tradeAggregation,
      ruleCount: rulesCount,
    });

    const timeBatches = generateBatchTimeRanges({ start, finish, batchSizeHours });

    const tradeSummaryPromise = buildTradeSummaryPromise({
      rulesCount,
      timeBatches,
      meters,
      environment,
      tradeAggregation,
      timezone,
      cancelledRef,
      stateDispatchFunc,
    });

    const [consumedData, generatedData, tradeSummaryBatches] = await Promise.all([
      consumedPromise,
      generatedPromise,
      tradeSummaryPromise,
    ]);

    // Check for cancellation after consumed/generated/tradeSummary fetch
    if (cancelledRef?.current) {
      logger.log('Time series data fetch cancelled after Stages 3-5');
      return;
    }

    // Skip final merge when there is no useful data to trade against:
    // - No meter data (historian returned null calculatedData for all meters)
    // - No trade rules (trades cannot exist without rules)
    if (!hasMeterData(consumedData, generatedData) || rulesCount === 0) {
      const reason = rulesCount === 0
        ? 'No trade rules found'
        : 'No meter data returned for any meter';
      handleSkippedTradeSummary({
        reason,
        meters,
        rulesData,
        consumedData,
        generatedData,
        initialData,
        stateDispatchFunc,
        onDataReceivedFunc: onDataReceived,
      });
      return;
    }

    // Merge energy data first (this ensures consumed/generated are updated)
    const dataWithEnergy = mergePropertyDataBatches(
      initialData,
      [buildMeterMergeBatch({ meters, consumedData, generatedData })],
    );

    const mergedData = mergePropertyDataBatches(dataWithEnergy, tradeSummaryBatches);

    if (onDataReceived) {
      onDataReceived(mergedData, tradeSummaryBatches);
    }
  } catch (error) {
    logger.error('Error fetching time-dependent data:', error);
    if (onError) {
      onError(error);
    } else if (stateDispatchFunc) {
      // onError is the primary error channel; dispatch CANCEL_REQUEST as a
      // fallback so the loading reducer reaches a terminal state even when
      // the caller omits onError, preventing a silent loading-state hang.
      stateDispatchFunc({ type: PropertyShowLoadingActions.CANCEL_REQUEST });
    }
  } finally {
    if (isFetchingRef) {
      isFetchingRef.current = false;
    }
  }
};

/**
 * Main function to fetch property data using five-stage approach with parallel rule count
 * Stage 1: Meters and rulesCount (run in parallel)
 * Stages 2-5: Rules, consumed data, generated data, and trade summary (run in parallel)
 * Batch size is calculated internally based on historian aggregation and rules count
 *
 * Loading state is managed via stateDispatchFunc which dispatches actions to the reducer.
 * @param {object} args - Arguments for the fetch operation
 * @param {string} args.propertyId - ID of the property to fetch
 * @param {object} args.dateSpan - Date span with start and finish properties (Luxon DateTimes)
 * @param {string} args.timezone - Timezone to use for data
 * @param {object} args.environment - Relay environment for GraphQL queries
 * @param {string} args.historianAggregation - Aggregation level for historian data
 * @param {string} args.tradeAggregation - Aggregation level for trade data
 * @param {object} args.initialData - Initial data to merge batches with
 * @param {Function} args.onDataReceived - Callback when all data is received and merged
 * @param {Function} args.onError - Callback when an error occurs
 * @param {Function} args.stateDispatchFunc - Function to dispatch loading state actions
 * @param {object} args.isFetchingRef - Ref to track if fetching is in progress
 * @param {object} [args.cancelledRef] - Ref to check if request has been cancelled
 */
export const getPropertyDataInBatches = async (args) => {
  const {
    propertyId,
    dateSpan,
    timezone,
    environment,
    historianAggregation,
    tradeAggregation,
    initialData,
    onDataReceived,
    onError,
    stateDispatchFunc,
    isFetchingRef,
    cancelledRef,
  } = args;

  // Safety check for required parameters
  if (!propertyId
    || !dateSpan
    || !timezone
    || !environment
    || !historianAggregation
    || !tradeAggregation) {
    if (onError) {
      onError(new Error('Missing required parameters for getPropertyDataInPartition'));
    }
    return;
  }

  // Prevent parallel fetches
  if (isFetchingRef && isFetchingRef.current) {
    logger.log('Skipping three-stage fetch - another fetch is already in progress');
    return;
  }

  if (isFetchingRef) {
    isFetchingRef.current = true;
  }

  try {
    const { start, finish } = dateSpan || {};

    // Check for cancellation before starting
    if (cancelledRef?.current) {
      logger.log('Property data fetch cancelled before starting');
      return;
    }

    // Stage 1: Fetch all meters and total rules count in parallel
    const [meters, rulesCount] = await Promise.all([
      getMeters({
        propertyId,
        environment,
        pageSize: METER_LIST_PAGE_SIZE,
      }),
      getPropertyRulesCount({
        propertyId,
        environment,
        startSeconds: Math.floor(start.toSeconds()),
        finishSeconds: Math.floor(finish.toSeconds()),
      }),
    ]);

    // Check for cancellation after Stage 1
    if (cancelledRef?.current) {
      logger.log('Property data fetch cancelled after Stage 1');
      return;
    }

    if (meters.length === 0) {
      logger.log('No meters found, returning empty data');
      if (stateDispatchFunc) {
        stateDispatchFunc({
          type: PropertyShowLoadingActions.SET_METERS_LOADED,
          metersCount: 0,
        });
        stateDispatchFunc({
          type: PropertyShowLoadingActions.SET_RULES_LOADED,
          rulesCount: 0,
        });
      }
      if (onDataReceived) {
        // Normalise the empty result so `property.meters.edges` is an explicit
        // array. The static PropertyShow fragment omits `meters`, so passing
        // `initialData` as-is would leave `edges` undefined and cause
        // `addSegmentToCache` to skip caching this valid zero-meter result.
        // That skip means the cancel/revert flow re-fetches on every cancel
        // (PT-2445); an explicit empty edges array makes the segment cacheable.
        const emptyMetersData = {
          ...initialData,
          property: {
            ...initialData?.property,
            meters: {
              ...initialData?.property?.meters,
              edges: [],
            },
          },
        };
        onDataReceived(emptyMetersData, []);
      }
      return;
    }

    // Notify stage 1 complete: meters and rule count loaded
    if (stateDispatchFunc) {
      stateDispatchFunc({
        type: PropertyShowLoadingActions.SET_METERS_LOADED,
        metersCount: meters.length,
      });
      stateDispatchFunc({
        type: PropertyShowLoadingActions.SET_RULES_LOADED,
        rulesCount,
      });
    }

    // Stages 2-5: Fetch rules, energy data, and trade summary in parallel.
    // Stage 5 uses the rulesCount from Stage 1 for batch sizing so it can
    // start immediately — no need to wait for Stage 2's actual rule edges.

    // Stage 2: Rules
    const rulesPromise = Promise.all(meters.map((meter) => getRules({
      meterId: meter.uuid,
      start,
      finish,
      environment,
      pageSize: TRADE_RULE_LIST_PAGE_SIZE,
    })));

    // Stage 3: Consumed meter data
    const consumedPromise = Promise.all(meters.map((meter) => getMeterData({
      meterId: meter.uuid,
      start,
      finish,
      environment,
      metric: ELEC_ENERGY_CONSUMED,
      historianAggregation,
      timezone,
    }))).then((data) => {
      if (stateDispatchFunc && !cancelledRef?.current) {
        stateDispatchFunc({ type: PropertyShowLoadingActions.SET_CONSUMED_DATA_LOADED });
      }
      return data;
    });

    // Stage 4: Generated meter data
    const generatedPromise = Promise.all(meters.map((meter) => getMeterData({
      meterId: meter.uuid,
      start,
      finish,
      environment,
      metric: ELEC_ENERGY_GENERATED,
      historianAggregation,
      timezone,
    }))).then((data) => {
      if (stateDispatchFunc && !cancelledRef?.current) {
        stateDispatchFunc({ type: PropertyShowLoadingActions.SET_GENERATED_DATA_LOADED });
      }
      return data;
    });

    // Stage 5: Trade Summary
    const { batchSizeHours } = calculateTradeSetSummaryBatchSize({
      start,
      finish,
      aggregation: tradeAggregation,
      ruleCount: rulesCount,
    });

    const timeBatches = generateBatchTimeRanges({ start, finish, batchSizeHours });

    const tradeSummaryPromise = buildTradeSummaryPromise({
      rulesCount,
      timeBatches,
      meters,
      environment,
      tradeAggregation,
      timezone,
      cancelledRef,
      stateDispatchFunc,
    });

    const [rulesData, consumedData, generatedData, tradeSummaryBatches] = await Promise.all([
      rulesPromise,
      consumedPromise,
      generatedPromise,
      tradeSummaryPromise,
    ]);

    // Check for cancellation after all parallel stages
    if (cancelledRef?.current) {
      logger.log('Property data fetch cancelled after Stages 2-5');
      return;
    }

    // Skip final merge when there is no useful data to trade against:
    // - No meter data (historian returned null calculatedData for all meters)
    // - No trade rules (trades cannot exist without rules)
    if (!hasMeterData(consumedData, generatedData) || rulesCount === 0) {
      const reason = rulesCount === 0
        ? 'No trade rules found'
        : 'No meter data returned for any meter';
      handleSkippedTradeSummary({
        reason,
        meters,
        rulesData,
        consumedData,
        generatedData,
        initialData,
        stateDispatchFunc,
        onDataReceivedFunc: onDataReceived,
      });
      return;
    }

    // Build base data structure with fetched meters, rules, and energy data.
    // Energy data (consumed/generated) must be merged here so downstream
    // consumers (extractData/buildMainData) can derive each meter's
    // aggregation — without it, trade data is dropped and the chart shows
    // "no trade data available for the selected time range".
    const dataWithMetersRulesAndEnergy = mergePropertyDataBatches(
      initialData,
      [buildMeterMergeBatch({
        meters, consumedData, generatedData, rulesData, includeRules: true,
      })],
    );

    const mergedData = mergePropertyDataBatches(dataWithMetersRulesAndEnergy, tradeSummaryBatches);

    if (onDataReceived) {
      onDataReceived(mergedData, tradeSummaryBatches);
    }
  } catch (error) {
    logger.error('Error in property data fetching:', error);
    if (onError) {
      onError(error);
    } else if (stateDispatchFunc) {
      // onError is the primary error channel; dispatch CANCEL_REQUEST as a
      // fallback so the loading reducer reaches a terminal state even when
      // the caller omits onError, preventing a silent loading-state hang.
      stateDispatchFunc({ type: PropertyShowLoadingActions.CANCEL_REQUEST });
    }
  } finally {
    if (isFetchingRef) {
      isFetchingRef.current = false;
    }
  }
};
